import type { ObjectDetector } from '@mediapipe/tasks-vision'

const MEDIAPIPE_VERSION = '0.10.18'
const WASM_CDN = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MEDIAPIPE_VERSION}/wasm`
const MODEL_URL =
    'https://storage.googleapis.com/mediapipe-models/object_detector/efficientdet_lite0/float32/1/efficientdet_lite0.tflite'

const DEFAULT_SCORE_THRESHOLD = 0.4
const DETECTOR_INIT_TIMEOUT_MS = 8000

/**
 * COCO classes that dominate score×area (e.g. a person holding a backpack)
 * but are useless for Casaideas product / object-type fallback search.
 */
const NON_PRODUCT_LABELS = new Set([
    'person',
    'people',
    'man',
    'woman',
    'boy',
    'girl',
    'face'
])

/**
 * When any of these is detected above threshold, prefer them over larger
 * background objects (chair, couch, dining table, etc.) by ranking on score only.
 */
const PREFERRED_PRODUCT_LABELS = new Set([
    'backpack',
    'handbag',
    'suitcase',
    'umbrella',
    'bottle',
    'cup',
    'bowl',
    'vase',
    'clock',
    'book',
    'scissors',
    'teddy bear',
    'potted plant'
])

let detectorPromise: Promise<ObjectDetector | null> | null = null

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> => {
    return new Promise((resolve, reject) => {
        const timer = window.setTimeout(() => {
            reject(new Error(`Timeout after ${ms}ms`))
        }, ms)

        promise
            .then((value) => {
                window.clearTimeout(timer)
                resolve(value)
            })
            .catch((error) => {
                window.clearTimeout(timer)
                reject(error)
            })
    })
}

/**
 * Lazy-load MediaPipe ObjectDetector (IMAGE mode). Cached after first success.
 * Returns null if WASM/model fail so callers can fall back to the full image.
 */
export const getVisualSearchObjectDetector =
    async (): Promise<ObjectDetector | null> => {
        if (typeof window === 'undefined') {
            return null
        }

        if (!detectorPromise) {
            detectorPromise = (async () => {
                try {
                    const { FilesetResolver, ObjectDetector } =
                        await import('@mediapipe/tasks-vision')
                    const vision = await withTimeout(
                        FilesetResolver.forVisionTasks(WASM_CDN),
                        DETECTOR_INIT_TIMEOUT_MS
                    )
                    const createDetector = (delegate: 'GPU' | 'CPU') =>
                        ObjectDetector.createFromOptions(vision, {
                            baseOptions: {
                                modelAssetPath: MODEL_URL,
                                delegate
                            },
                            scoreThreshold: DEFAULT_SCORE_THRESHOLD,
                            // Person often tops the list; keep room for backpack/handbag.
                            maxResults: 10,
                            runningMode: 'IMAGE'
                        })

                    try {
                        return await withTimeout(
                            createDetector('GPU'),
                            DETECTOR_INIT_TIMEOUT_MS
                        )
                    } catch {
                        return await withTimeout(
                            createDetector('CPU'),
                            DETECTOR_INIT_TIMEOUT_MS
                        )
                    }
                } catch (error) {
                    console.warn(
                        '[visual-search] ObjectDetector init failed, using full image:',
                        error
                    )
                    detectorPromise = null
                    return null
                }
            })()
        }

        return detectorPromise
    }

export type PrimaryObjectBox = {
    originX: number
    originY: number
    width: number
    height: number
    score: number
    /** Etiqueta COCO en inglés (p.ej. "backpack"), si el detector la expone. */
    categoryName?: string
}

export type RankableDetection = {
    originX: number
    originY: number
    width: number
    height: number
    score: number
    categoryName?: string
}

const toPrimaryBox = (
    detection: RankableDetection,
    categoryName?: string
): PrimaryObjectBox => ({
    originX: detection.originX,
    originY: detection.originY,
    width: detection.width,
    height: detection.height,
    score: detection.score,
    ...(categoryName ? { categoryName } : {})
})

/**
 * Picks the best product-like detection.
 * - Ignores "person" (and similar) so they never become object_label
 * - If a preferred product label (backpack, handbag, …) is present, pick the
 *   highest-score one among those (area would otherwise favor furniture)
 * - Otherwise pick best score × area among remaining detections
 * Exported for unit tests.
 */
export const pickPrimaryProductDetection = (
    detections: RankableDetection[],
    scoreThreshold = DEFAULT_SCORE_THRESHOLD
): PrimaryObjectBox | null => {
    const eligible: Array<RankableDetection & { categoryName?: string }> = []

    for (const detection of detections) {
        if (detection.width <= 0 || detection.height <= 0) {
            continue
        }
        if (detection.score < scoreThreshold) {
            continue
        }

        const categoryName = detection.categoryName?.trim().toLowerCase()
        if (categoryName && NON_PRODUCT_LABELS.has(categoryName)) {
            continue
        }

        eligible.push({
            ...detection,
            ...(categoryName ? { categoryName } : {})
        })
    }

    if (eligible.length === 0) {
        return null
    }

    const preferred = eligible.filter(
        (d) => d.categoryName && PREFERRED_PRODUCT_LABELS.has(d.categoryName)
    )

    const pool = preferred.length > 0 ? preferred : eligible
    let best: PrimaryObjectBox | null = null
    let bestRank = -1

    for (const detection of pool) {
        const rank =
            preferred.length > 0
                ? detection.score
                : detection.score * detection.width * detection.height

        if (rank > bestRank) {
            bestRank = rank
            best = toPrimaryBox(detection, detection.categoryName)
        }
    }

    return best
}

/**
 * Picks the detection with the best product-aware score × area product.
 */
export const detectPrimaryObjectBox = async (
    image: HTMLImageElement | ImageBitmap | HTMLCanvasElement,
    scoreThreshold = DEFAULT_SCORE_THRESHOLD
): Promise<PrimaryObjectBox | null> => {
    const detector = await getVisualSearchObjectDetector()
    if (!detector) {
        return null
    }

    try {
        const result = detector.detect(image)
        const detections = result?.detections ?? []
        const rankable: RankableDetection[] = []

        for (const detection of detections) {
            const box = detection.boundingBox
            if (!box || box.width <= 0 || box.height <= 0) {
                continue
            }

            const category = detection.categories?.[0]
            const score = category?.score ?? 0
            const categoryName =
                typeof category?.categoryName === 'string'
                    ? category.categoryName.trim().toLowerCase()
                    : undefined

            rankable.push({
                originX: box.originX,
                originY: box.originY,
                width: box.width,
                height: box.height,
                score,
                ...(categoryName ? { categoryName } : {})
            })
        }

        const primary = pickPrimaryProductDetection(rankable, scoreThreshold)
        if (
            !primary &&
            rankable.some(
                (d) => d.categoryName && NON_PRODUCT_LABELS.has(d.categoryName)
            )
        ) {
            console.info(
                '[visual-search] only non-product detections (e.g. person); using full image without object_label'
            )
        }

        return primary
    } catch (error) {
        console.warn(
            '[visual-search] detect() failed, using full image:',
            error
        )
        return null
    }
}
